import React, { useState, useEffect, useCallback } from 'react';
import { getEtfFlows, getEtfFlowSummary, getEtfFlowAssets } from '../api';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../hooks/useIsMobile';

// Daily net creation/redemption flow for US spot crypto ETFs. Positive = net
// money IN (bullish demand signal), negative = net OUT. BTC comes from
// TFTC's free feed (aggregating SoSoValue + Farside); every other asset
// comes from SoSoValue's own API directly — see backend/services/
// etfFlowClient.js and sosoValueClient.js.
function fmtUsd(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e9) return `$${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `$${(abs / 1e3).toFixed(1)}K`;
  return `$${abs.toFixed(0)}`;
}
function fmtUsdSigned(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : n < 0 ? '-' : '';
  return `${sign}${fmtUsd(n)}`;
}

const ETF_ASSET_LABEL = {
  BTC: 'Bitcoin', ETH: 'Ethereum', SOL: 'Solana', XRP: 'XRP', DOGE: 'Dogecoin',
  LINK: 'Chainlink', LTC: 'Litecoin', AVAX: 'Avalanche', HBAR: 'Hedera', DOT: 'Polkadot',
  NEAR: 'NEAR', BNB: 'BNB', TRX: 'TRON', ZEC: 'Zcash', HYPE: 'Hyperliquid',
};

// Gross buy/sell for one asset's latest day. When a per-fund breakdown
// exists (BTC, via TFTC), some funds can be creating while others redeem on
// the very same day — summing each direction separately across funds gives
// the real gross totals, not just the single net figure. Assets without a
// breakdown (SoSoValue-sourced) only ever report one net number per day, so
// it can only be attributed entirely to buy or entirely to sell.
function buySellForLatest(latest) {
  if (!latest) return { buy: 0, sell: 0 };
  const perFund = latest.perFundUsd
    ? Object.entries(latest.perFundUsd).filter(([, v]) => typeof v === 'number' && v !== 0)
    : [];
  if (perFund.length > 0) {
    return perFund.reduce((acc, [, v]) => (v > 0 ? { ...acc, buy: acc.buy + v } : { ...acc, sell: acc.sell - v }), { buy: 0, sell: 0 });
  }
  return {
    buy: latest.netFlowUsd > 0 ? latest.netFlowUsd : 0,
    sell: latest.netFlowUsd < 0 ? -latest.netFlowUsd : 0,
  };
}

// Diverging bar chart around a zero baseline — green bars above for net
// inflow days, red bars below for net outflow days.
function NetFlowBarChart({ rows, height = 220 }) {
  const { t } = useTranslation();
  if (rows.length === 0) return null;
  const W = 900, padL = 46, padR = 6, padT = 10, padB = 20;
  const plotW = W - padL - padR, plotH = height - padT - padB;
  const maxAbs = Math.max(1, ...rows.map((r) => Math.abs(r.netFlowUsd || 0)));
  const groupW = plotW / rows.length;
  const barW = Math.max(1, groupW - 1.5);
  const xPos = (i) => padL + i * groupW;
  const zeroY = padT + plotH / 2;
  const yPos = (v) => zeroY - (v / maxAbs) * (plotH / 2);
  const labelEvery = Math.max(1, Math.ceil(rows.length / 8));

  return (
    <svg viewBox={`0 0 ${W} ${height}`} width="100%" height={height} preserveAspectRatio="none" style={{ display: 'block' }}>
      {[maxAbs, 0, -maxAbs].map((v) => (
        <g key={v}>
          <line x1={padL} x2={W - padR} y1={yPos(v)} y2={yPos(v)} stroke={v === 0 ? 'var(--text-dim)' : 'var(--border)'} strokeWidth={1} strokeDasharray={v === 0 ? '2,2' : '3,3'} />
          <text x={padL - 6} y={yPos(v) + 3} fontSize={10} fill="var(--text-dim)" textAnchor="end">{fmtUsd(v)}</text>
        </g>
      ))}
      {rows.map((r, i) => {
        const v = r.netFlowUsd || 0;
        const y0 = yPos(0), y1 = yPos(v);
        return (
          <rect
            key={r.date} x={xPos(i)} y={Math.min(y0, y1)} width={barW}
            height={Math.max(0.5, Math.abs(y1 - y0))}
            fill={v >= 0 ? '#10b981' : '#ef4444'} opacity={0.85}
          >
            <title>{`${r.date}: ${fmtUsdSigned(v)}${r.missingFunds?.length ? t('etf.barMissing', { funds: r.missingFunds.join(', ') }) : ''}`}</title>
          </rect>
        );
      })}
      {rows.map((r, i) => (i % labelEvery === 0 ? (
        <text key={r.date} x={xPos(i) + groupW / 2} y={height - 5} fontSize={10} fill="var(--text-dim)" textAnchor="middle">{r.date.slice(5)}</text>
      ) : null))}
    </svg>
  );
}

export default function EtfFlowPage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [assets, setAssets] = useState(['BTC']);
  const [asset, setAsset] = useState('BTC');
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [ready, setReady] = useState(false);
  // Cross-asset totals — every tracked asset's latest day, summed. Loaded
  // once per asset-list refresh, independent of which single asset tab is
  // selected below (each asset can be on a different "latest" date, since
  // they sync on their own schedules — not a single shared "today").
  const [allTotals, setAllTotals] = useState(null);
  const [allTotalsReady, setAllTotalsReady] = useState(false);

  useEffect(() => { getEtfFlowAssets().then((r) => { if (r.assets?.length) setAssets(r.assets); }).catch(() => {}); }, []);

  useEffect(() => {
    let active = true;
    setAllTotalsReady(false);
    Promise.all(assets.map((a) => getEtfFlowSummary(a).catch(() => null)))
      .then((results) => {
        if (!active) return;
        let buy = 0, sell = 0, latestDate = null;
        for (const r of results) {
          if (!r?.latest) continue;
          const bs = buySellForLatest(r.latest);
          buy += bs.buy; sell += bs.sell;
          if (!latestDate || r.latest.date > latestDate) latestDate = r.latest.date;
        }
        setAllTotals({ buy, sell, latestDate });
      })
      .finally(() => { if (active) setAllTotalsReady(true); });
    return () => { active = false; };
  }, [assets]);

  const load = useCallback(async () => {
    setReady(false);
    try {
      const [sumRes, histRes] = await Promise.all([
        getEtfFlowSummary(asset),
        getEtfFlows(asset, 180),
      ]);
      setSummary(sumRes);
      setHistory(histRes.docs || []);
    } catch { setSummary(null); setHistory([]); }
    finally { setReady(true); }
  }, [asset]);

  useEffect(() => { load(); const t = setInterval(load, 6 * 60 * 60 * 1000); return () => clearInterval(t); }, [load]);

  const pageStyle = isMobile ? { ...s.page, padding: '16px 12px' } : s.page;
  const perFund = summary?.latest?.perFundUsd
    ? Object.entries(summary.latest.perFundUsd).filter(([, v]) => typeof v === 'number').sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))
    : [];
  const missingLatest = summary?.latest?.missingFunds || [];
  // Funds never covered on any shown day (e.g. HYPG) — named once under the chart.
  const alwaysMissing = history.length
    ? missingLatest.filter((t) => history.every((r) => r.missingFunds?.includes(t)))
    : [];

  return (
    <div style={pageStyle}>
      <div style={s.header}>
        <h2 style={s.title}>{t('etf.title')}</h2>
        <p style={s.subtitle}>{t('etf.subtitle')}</p>
      </div>

      <div style={s.notice}>
        {t('etf.notice')}
      </div>

      <div style={s.allCards}>
        <div style={s.card}>
          <span style={s.cardLabel}>
            {t('etf.totalBuy', { count: assets.length })}{allTotals?.latestDate ? ` (${allTotals.latestDate})` : ''}
          </span>
          <span style={{ ...s.cardValue, color: '#10b981' }}>
            {allTotalsReady ? fmtUsd(allTotals?.buy) : '…'}
          </span>
        </div>
        <div style={s.card}>
          <span style={s.cardLabel}>
            {t('etf.totalSell', { count: assets.length })}{allTotals?.latestDate ? ` (${allTotals.latestDate})` : ''}
          </span>
          <span style={{ ...s.cardValue, color: '#ef4444' }}>
            {allTotalsReady ? fmtUsd(allTotals?.sell) : '…'}
          </span>
        </div>
      </div>

      <div style={s.assetBar}>
        {assets.map((a) => (
          <button
            key={a}
            style={a === asset ? s.assetBtnActive : s.assetBtn}
            onClick={() => setAsset(a)}
          >
            {a}
          </button>
        ))}
      </div>

      {!ready ? (
        <div style={s.loading}>{t('etf.loading')}</div>
      ) : !summary?.latest ? (
        <div style={s.empty}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>💵</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>{t('etf.noData')}</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 460, textAlign: 'center' }}>
            {t('etf.noDataDesc', { asset: ETF_ASSET_LABEL[asset] || asset })}
          </div>
        </div>
      ) : (
        <>
          <div style={s.cards}>
            <div style={s.card}>
              <span style={s.cardLabel}>{t('etf.latestDay', { date: summary.latest.date })}</span>
              <span style={{ ...s.cardValue, color: summary.latest.netFlowUsd >= 0 ? '#10b981' : '#ef4444' }}>
                {fmtUsdSigned(summary.latest.netFlowUsd)}
              </span>
              {missingLatest.length > 0 && <span style={s.partialNote}>{t('etf.excludes', { funds: missingLatest.join(', ') })}</span>}
            </div>
            <div style={s.card}>
              <span style={s.cardLabel}>{asset === 'BTC' ? t('etf.cumulativeBtc') : t('etf.cumulative')}</span>
              <span style={{ ...s.cardValue, color: summary.cumulativeNetFlowUsd >= 0 ? '#10b981' : '#ef4444' }}>
                {fmtUsdSigned(summary.cumulativeNetFlowUsd)}
              </span>
            </div>
            <div style={s.card}>
              <span style={s.cardLabel}>{t('etf.aum')}</span>
              <span style={s.cardValue}>{fmtUsd(summary.latest.totalNetAssetsUsd)}</span>
              {missingLatest.length > 0 && <span style={s.partialNote}>{t('etf.excludes', { funds: missingLatest.join(', ') })}</span>}
            </div>
            <div style={s.card}>
              <span style={s.cardLabel}>{t('etf.daysOutIn')}</span>
              <span style={s.cardValue}>
                <span style={{ color: '#ef4444' }}>{summary.daysNegative}</span> / <span style={{ color: '#10b981' }}>{summary.daysPositive}</span>
              </span>
            </div>
          </div>

          {history.length > 0 && (
            <div style={s.chartCard}>
              <div style={s.chartTitle}>{t('etf.chartTitle', { asset: ETF_ASSET_LABEL[asset] || asset })}</div>
              <NetFlowBarChart rows={history} height={220} />
              <div style={s.legend}>
                <span style={{ color: '#10b981', fontWeight: 700 }}>{t('etf.inflow')}</span>
                <span style={{ color: '#ef4444', fontWeight: 700 }}>{t('etf.outflow')}</span>
              </div>
              {alwaysMissing.length > 0 && (
                <div style={s.partialBanner}>
                  {t('etf.partial', { asset, funds: alwaysMissing.join(', ') })}
                </div>
              )}
            </div>
          )}

          {(perFund.length > 0 || missingLatest.length > 0) && summary.latest.perFundUsd && (
            <div style={s.fundSection}>
              <div style={s.chartTitle}>{t('etf.breakdown', { date: summary.latest.date })}</div>
              <div style={s.fundGrid}>
                {perFund.map(([ticker, v]) => (
                  <div key={ticker} style={s.fundChip}>
                    <span style={s.fundTicker}>{ticker}</span>
                    <span style={{ color: v >= 0 ? '#10b981' : '#ef4444', fontWeight: 700 }}>{fmtUsdSigned(v)}</span>
                  </div>
                ))}
                {missingLatest.map((ticker) => (
                  <div key={ticker} style={{ ...s.fundChip, opacity: 0.6 }} title={t('etf.fundNoDataTitle')}>
                    <span style={s.fundTicker}>{ticker}</span>
                    <span style={{ color: 'var(--text-dim)', fontWeight: 700 }}>{t('etf.fundNoData')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p style={s.note}>
            {t('etf.note', { asset })}
          </p>
        </>
      )}
    </div>
  );
}

const s = {
  page: { padding: '24px 32px', maxWidth: 1100, margin: '0 auto' },
  header: { marginBottom: 12 },
  title: { margin: '0 0 4px', fontSize: 20, fontWeight: 800, color: 'var(--text)' },
  subtitle: { margin: 0, fontSize: 13, color: 'var(--text-muted)' },

  notice: {
    fontSize: 11, color: 'var(--text-muted)', background: 'var(--surface)',
    border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px',
    marginBottom: 16, lineHeight: 1.6,
  },

  allCards: { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 },

  assetBar: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 },
  assetBtn: {
    fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', background: 'var(--surface)',
    border: '1px solid var(--border)', borderRadius: 8, padding: '7px 14px', cursor: 'pointer',
  },
  assetBtnActive: {
    fontSize: 12.5, fontWeight: 700, color: '#fff', background: 'var(--accent)',
    border: '1px solid var(--accent)', borderRadius: 8, padding: '7px 14px', cursor: 'pointer',
  },

  loading: { color: 'var(--text-muted)', textAlign: 'center', padding: 60, fontSize: 14 },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '70px 20px' },

  cards: { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 },
  card: { flex: '1 1 200px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 4 },
  cardLabel: { fontSize: 9.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 },
  cardValue: { fontSize: 18, fontWeight: 800, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' },

  chartCard: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '16px 18px', marginBottom: 18 },
  chartTitle: { fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 10 },
  legend: { display: 'flex', gap: 16, fontSize: 11.5, marginTop: 8 },

  fundSection: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '16px 18px', marginBottom: 18 },
  fundGrid: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  fundChip: {
    display: 'flex', alignItems: 'center', gap: 6, background: 'var(--bg)', border: '1px solid var(--border)',
    borderRadius: 8, padding: '6px 12px', fontSize: 12,
  },
  fundTicker: { fontWeight: 700, color: 'var(--text)' },

  note: { fontSize: 10.5, color: 'var(--text-dim)', margin: '10px 0 0', fontStyle: 'italic' },
  partialNote: { fontSize: 10.5, fontWeight: 700, color: '#f59e0b' },
  partialBanner: { marginTop: 10, padding: '8px 12px', borderRadius: 8, fontSize: 12, lineHeight: 1.5, color: 'var(--text)', background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.45)' },
};
